import {
  RpcDisconnectedError,
  RpcRemoteError,
  RpcTimeoutError,
  type RpcCall,
  type RpcCallOptions,
  type RpcReply,
  type RpcService,
} from './rpcTypes';
import {
  NOOP_TELEMETRY_REPORTER,
  reportTelemetrySafely,
  type TelemetryReporter,
} from '@/telemetry';

type DataChannelLike = Pick<RTCDataChannel, 'readyState' | 'send'>;
type Pending = {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  timeoutId: ReturnType<typeof setTimeout>;
  signal?: AbortSignal;
  abortHandler?: () => void;
  startedAt: number;
  connectionSessionId?: string;
  flowRunId?: string;
  nodeId?: string;
};

export const DEFAULT_RPC_TIMEOUT_MS = 65_000;
const CORRELATION_ID_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;
const RPC_ERROR_CODES = new Set([
  'INVALID_REQUEST',
  'UNKNOWN_SERVICE',
  'ROBOT_NOT_READY',
  'SPEECH_FAILED',
  'MOTION_FAILED',
  'TOF_FAILED',
  'VOLUME_FAILED',
  'CAMERA_FAILED',
  'MICROPHONE_FAILED',
  'SOUND_FAILED',
  'LED_FAILED',
  'RPC_TIMEOUT',
  'INTERNAL_ERROR',
]);
const hasOwn = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);
const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

export const isRpcReply = (value: unknown): value is RpcReply => {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  if (keys.some((key) => !['action', 'qId', 'data', 'error'].includes(key))) return false;
  if (value.action !== 'reply' || typeof value.qId !== 'string' || !CORRELATION_ID_PATTERN.test(value.qId)) return false;
  const hasData = hasOwn(value, 'data');
  const hasError = hasOwn(value, 'error');
  if (hasData === hasError) return false;
  if (!hasError) return true;
  const error = value.error;
  if (!isRecord(error) || Object.keys(error).some((key) => !['code', 'message', 'retryable'].includes(key))) return false;
  return (
    typeof error.code === 'string'
    && RPC_ERROR_CODES.has(error.code)
    && typeof error.message === 'string'
    && error.message.length >= 1
    && error.message.length <= 512
    && typeof error.retryable === 'boolean'
  );
};

export class RobotRpcClient {
  private channel: DataChannelLike | null = null;
  private pending = new Map<string, Pending>();
  private generation = 0;

  constructor(
    readonly robotId: string,
    private readonly telemetryReporter: TelemetryReporter = NOOP_TELEMETRY_REPORTER,
    private readonly getConnectionSessionId: () => string | undefined = () => undefined,
  ) {}

  setDataChannel(channel: DataChannelLike | null, generation: number) {
    if (generation !== this.generation || channel === null) this.clearPending(new RpcDisconnectedError(this.robotId));
    this.generation = generation;
    this.channel = channel;
  }

  call<T = unknown>(service: RpcService, data?: unknown, options: RpcCallOptions = {}): Promise<T> {
    const startedAt = performance.now();
    const qId = `q-${crypto.randomUUID()}`;
    const channel = this.channel;
    if (!channel || channel.readyState !== 'open') {
      const error = new RpcDisconnectedError(this.robotId);
      this.reportRpcFailure(qId, {
        startedAt,
        connectionSessionId: this.getConnectionSessionId(),
        flowRunId: options.flowRunId,
        nodeId: options.nodeId,
      }, error);
      return Promise.reject(error);
    }
    const callGeneration = this.generation;
    const payload: RpcCall = {
      action: 'call',
      qId,
      params: { service, ...(data === undefined ? {} : { data }) },
      ...(options.flowRunId ? { flowRunId: options.flowRunId } : {}),
      ...(options.nodeId ? { nodeId: options.nodeId } : {}),
    };

    return new Promise<T>((resolve, reject) => {
      const finishReject = (reason: unknown) => {
        const entry = this.pending.get(qId);
        if (!entry) return;
        this.removePending(qId, entry);
        this.reportRpcFailure(qId, entry, reason);
        reject(reason);
      };
      const timeoutId = setTimeout(() => finishReject(new RpcTimeoutError(this.robotId)), options.timeoutMs ?? DEFAULT_RPC_TIMEOUT_MS);
      const abortHandler = options.signal ? () => finishReject(new DOMException('Aborted', 'AbortError')) : undefined;
      const entry: Pending = {
        resolve: (value) => resolve(value as T),
        reject,
        timeoutId,
        signal: options.signal,
        abortHandler,
        startedAt,
        connectionSessionId: this.getConnectionSessionId(),
        flowRunId: options.flowRunId,
        nodeId: options.nodeId,
      };
      this.pending.set(qId, entry);
      if (options.signal?.aborted) {
        finishReject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      options.signal?.addEventListener('abort', abortHandler!, { once: true });
      try {
        if (callGeneration !== this.generation || this.channel !== channel) throw new RpcDisconnectedError(this.robotId);
        channel.send(JSON.stringify(payload));
      } catch (error) {
        finishReject(error);
      }
    });
  }

  handleMessage(raw: unknown, generation: number): boolean {
    if (generation !== this.generation) return false;
    try {
      const payload: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!isRpcReply(payload)) return false;
      const entry = this.pending.get(payload.qId);
      if (!entry) return false;
      this.removePending(payload.qId, entry);
      if ('error' in payload && payload.error !== undefined) {
        const error = new RpcRemoteError(this.robotId, payload.error);
        this.reportRpcFailure(payload.qId, entry, error);
        entry.reject(error);
      } else {
        reportTelemetrySafely(this.telemetryReporter, {
          eventType: 'rpc.succeeded',
          robotId: this.robotId,
          connectionSessionId: entry.connectionSessionId,
          flowRunId: entry.flowRunId,
          nodeId: entry.nodeId,
          requestId: payload.qId,
          durationMs: performance.now() - entry.startedAt,
        });
        entry.resolve(payload.data);
      }
      return true;
    } catch {
      return false;
    }
  }

  clearPending(reason: unknown = new RpcDisconnectedError(this.robotId)) {
    const entries = [...this.pending.entries()];
    this.pending.clear();
    entries.forEach(([qId, entry]) => {
      clearTimeout(entry.timeoutId);
      if (entry.signal && entry.abortHandler) entry.signal.removeEventListener('abort', entry.abortHandler);
      this.reportRpcFailure(qId, entry, reason);
      entry.reject(reason);
    });
  }

  dispose() {
    this.channel = null;
    this.generation += 1;
    this.clearPending();
  }

  private removePending(qId: string, entry: Pending) {
    this.pending.delete(qId);
    clearTimeout(entry.timeoutId);
    if (entry.signal && entry.abortHandler) entry.signal.removeEventListener('abort', entry.abortHandler);
  }

  private reportRpcFailure(
    requestId: string,
    context: Pick<Pending, 'startedAt' | 'connectionSessionId' | 'flowRunId' | 'nodeId'>,
    reason: unknown,
  ) {
    const isTimeout = reason instanceof RpcTimeoutError;
    const errorCode = reason instanceof RpcRemoteError
      ? reason.payload.code
      : isTimeout
        ? 'RPC_TIMEOUT'
        : reason instanceof RpcDisconnectedError
          ? 'DISCONNECTED'
          : reason instanceof DOMException && reason.name === 'AbortError'
            ? 'ABORTED'
            : 'RPC_FAILED';
    reportTelemetrySafely(this.telemetryReporter, {
      eventType: isTimeout ? 'rpc.timeout' : 'rpc.failed',
      robotId: this.robotId,
      connectionSessionId: context.connectionSessionId,
      flowRunId: context.flowRunId,
      nodeId: context.nodeId,
      requestId,
      durationMs: performance.now() - context.startedAt,
      errorCode,
    });
  }
}
